import { AF_AU, type ClubwiseBrandRules } from './brands.js';
import { classifyDigital, classifyStatus, cleanCell, cleanLabelCell, looksLikePii, type DigitalClass, type LeadChannel } from './classify.js';
import { isValidClubwiseCode, memberMatchesClubCode } from './clubCode.js';
import { isMonthKey, monthKeyOfDay, monthLastDay, parseClubwiseDate } from './months.js';
import type { ClubwiseJoinRow, ClubwiseLeadRow } from './csv.js';

// Validation + preview for ONE club-month ClubWise upload. PURE and shared: the
// browser runs it to show the preview, and the server runs it again on exactly
// what it receives (the browser is not trusted). It sees only the whitelisted
// columns, so it cannot leak anything it never had.

export interface ClubwiseUploadPayload {
  /** YYYY-MM — the month the exports were run for. */
  month: string;
  /**
   * YYYY-MM-DD — the last day the exports cover. ⚠️ Absent/null = the whole month
   * (what every upload meant before this existed). For the current month it is the
   * day the export was run; leads have no day, so this is the only record of how far
   * "this month's leads" go.
   */
  dataTo?: string | null;
  leads: ClubwiseLeadRow[];
  joins: ClubwiseJoinRow[];
}

/** The default "data up to" for a month: the upload day for the current month, else the month's last day. */
export function defaultDataTo(month: string, today: string): string {
  if (!isMonthKey(month)) return today;
  return month === today.slice(0, 7) ? today : monthLastDay(month);
}

export interface ValueCount {
  value: string;
  count: number;
}

export interface SourceCount extends ValueCount {
  isDigital: boolean;
  digitalClass: DigitalClass | null;
}

export interface ContactTypeCount extends ValueCount {
  isDigital: boolean;
}

/** A Source / Contact Type pair that fell to OTHER, so staff can spot a new spelling. */
export interface OtherValueCount {
  source: string;
  contactType: string;
  count: number;
}

export interface ClubwiseUploadAnalysis {
  /**
   * ⚠️ Source / Contact Type cells that looked like a phone number or email and were
   * removed. A COUNT only: the value is never stored, shown or logged.
   */
  valuesRemoved: number;
  /** Source / Contact Type pairs (leads and joins) that landed in OTHER, with counts. */
  otherValues: OtherValueCount[];
  /**
   * ClubWise TEST records (email ends @clubwise.com) dropped before anything was
   * counted, leads and joiners together. A count only.
   */
  testRecordsSkipped: number;
  /** Blocking problems. Any entry means the upload cannot be committed. */
  errors: string[];
  /** Non-blocking things the uploader should read before confirming. */
  warnings: string[];
  counts: {
    leads: number;
    /** "Sales": rows in the Joiners file. */
    monthJoins: number;
    /** This month's leads who joined (member number also in the Joiners file). */
    cohortJoins: number;
    /** Leads whose Status says joined — a cross-check on `cohortJoins`. */
    leadsWithJoinedStatus: number;
    digitalLeads: number;
    digitalJoins: number;
  };
  duplicates: { leads: number; joins: number };
  /** Statuses we do not recognise, SHOWN rather than classified. */
  unknownStatuses: ValueCount[];
  /** Leads with a blank status. */
  blankStatus: number;
  sources: SourceCount[];
  contactTypes: ContactTypeCount[];
}

export interface PreparedLead {
  memberNumber: string;
  status: string;
  source: string;
  contactType: string;
  isDigital: boolean;
  digitalClass: DigitalClass | null;
  /** Exactly one per lead; decided at upload so every read agrees. */
  channel: LeadChannel;
  /** A digital Contact Type with a blank Source. */
  untagged: boolean;
  /** The label the sources breakdown groups by ('' = not recorded). */
  sourceLabel: string;
  isCohortJoin: boolean;
}

export interface PreparedJoin {
  memberNumber: string;
  status: string;
  source: string;
  contactType: string;
  membershipType: string;
  /** YYYY-MM-DD */
  joiningDate: string;
  isDigital: boolean;
  digitalClass: DigitalClass | null;
  channel: LeadChannel;
  sourceLabel: string;
  /** This join's member is among the SAME month's leads (otherwise an earlier lead). */
  fromMonthLead: boolean;
}

export interface ClubwiseUploadResult {
  analysis: ClubwiseUploadAnalysis;
  /** Present only when `analysis.errors` is empty. Deduped. `dataTo` null = the whole month. */
  prepared: { leads: PreparedLead[]; joins: PreparedJoin[]; dataTo: string | null } | null;
}

function tally<T>(items: readonly T[], key: (t: T) => string): Map<string, number> {
  const m = new Map<string, number>();
  for (const it of items) {
    const k = key(it);
    m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

const byCountThenName = (a: ValueCount, b: ValueCount): number =>
  b.count - a.count || a.value.localeCompare(b.value);

/** Dedupe by member number, KEEP LAST (a later row supersedes an earlier one). */
function dedupeByMember<T extends { memberNumber: string }>(rows: readonly T[]): { rows: T[]; dropped: number } {
  const byMember = new Map<string, T>();
  for (const r of rows) byMember.set(r.memberNumber.toUpperCase(), r);
  return { rows: [...byMember.values()], dropped: rows.length - byMember.size };
}

/**
 * Validate and summarise one club-month upload.
 *
 * ⚠️ Every blocking check REFUSES rather than repairs, and none echoes a member
 * number — messages carry counts only.
 */
export function analyseClubwiseUpload(
  payload: ClubwiseUploadPayload,
  clubCode: string,
  opts: { currentMonth?: string; today?: string; brand?: ClubwiseBrandRules } = {},
): ClubwiseUploadResult {
  // ⚠️ AU is the default so every pre-existing caller behaves exactly as before.
  const brand = opts.brand ?? AF_AU;
  const errors: string[] = [];
  const warnings: string[] = [];
  const code = clubCode.trim().toUpperCase();

  if (!isValidClubwiseCode(code)) {
    errors.push('This account has no valid ClubWise code. Set it on the account first.');
  }
  if (!isMonthKey(payload.month)) {
    errors.push('Choose the month these exports were run for.');
  } else if (opts.currentMonth && payload.month > opts.currentMonth) {
    errors.push('That month has not started yet.');
  }

  // ⚠️ "Data up to": inside the month, not in the future. A missing value is the whole month.
  let dataTo: string | null = null;
  if (isMonthKey(payload.month) && payload.dataTo) {
    if (parseClubwiseDate(payload.dataTo.split('-').reverse().join('/')) === null) {
      errors.push('"Data up to" is not a real date.');
    } else if (monthKeyOfDay(payload.dataTo) !== payload.month) {
      errors.push(`"Data up to" must fall inside ${payload.month}.`);
    } else if (opts.today && payload.dataTo > opts.today) {
      errors.push('"Data up to" cannot be in the future.');
    } else {
      dataTo = payload.dataTo;
    }
  }

  // ⚠️ ClubWise test records are removed FIRST, so nothing below (member-prefix check,
  // dedupe, cohort join, counts) ever sees them. Both files, both brands.
  const realLeads = payload.leads.filter((r) => r.testRecord !== true);
  const realJoins = payload.joins.filter((r) => r.testRecord !== true);
  const testRecordsSkipped = payload.leads.length - realLeads.length + (payload.joins.length - realJoins.length);
  if (testRecordsSkipped > 0) {
    warnings.push(`${testRecordsSkipped} ClubWise test record${testRecordsSkipped === 1 ? '' : 's'} skipped (not counted).`);
  }

  const leadsIn = realLeads.map((r) => ({
    memberNumber: cleanCell(r.memberNumber),
    status: cleanCell(r.status),
    source: cleanLabelCell(r.source),
    contactType: cleanLabelCell(r.contactType),
  }));
  const joinsIn = realJoins.map((r) => ({
    memberNumber: cleanCell(r.memberNumber),
    status: cleanCell(r.status),
    source: cleanLabelCell(r.source),
    contactType: cleanLabelCell(r.contactType),
    membershipType: cleanCell(r.membershipType),
    joiningDate: cleanCell(r.joiningDate),
  }));

  if (leadsIn.length === 0) {
    errors.push('The New Prospects file has no rows. Check it is the right file for this month.');
  }
  if (leadsIn.some((r) => r.memberNumber === '') || joinsIn.some((r) => r.memberNumber === '')) {
    errors.push('Some rows have no member number.');
  }

  // Wrong club: every member number must carry this club's prefix.
  if (isValidClubwiseCode(code)) {
    const bad =
      leadsIn.filter((r) => !memberMatchesClubCode(r.memberNumber, code)).length +
      joinsIn.filter((r) => !memberMatchesClubCode(r.memberNumber, code)).length;
    if (bad > 0) {
      errors.push(
        `${bad} member number${bad === 1 ? ' does' : 's do'} not start with this club's code (${code}). These look like another club's files.`,
      );
    }
  }

  // Joining dates: real dates, inside the chosen month.
  let unparseable = 0;
  let outsideMonth = 0;
  const joinDates = new Map<number, string>();
  joinsIn.forEach((r, i) => {
    const iso = parseClubwiseDate(r.joiningDate);
    if (iso === null) {
      unparseable += 1;
      return;
    }
    if (isMonthKey(payload.month) && monthKeyOfDay(iso) !== payload.month) outsideMonth += 1;
    joinDates.set(i, iso);
  });
  if (unparseable > 0) {
    errors.push(
      `${unparseable} Joining Date${unparseable === 1 ? ' is' : 's are'} missing or not in dd/mm/yyyy form. Check this is the Joiners file.`,
    );
  }
  if (dataTo) {
    const pastCutoff = [...joinDates.values()].filter((d) => monthKeyOfDay(d) === payload.month && d > (dataTo as string)).length;
    if (pastCutoff > 0) {
      errors.push(
        `${pastCutoff} Joining Date${pastCutoff === 1 ? ' falls' : 's fall'} after "Data up to" (${dataTo}). Move that date later, or check the Joiners file.`,
      );
    }
  }
  if (outsideMonth > 0) {
    errors.push(
      `${outsideMonth} Joining Date${outsideMonth === 1 ? ' falls' : 's fall'} outside ${payload.month}. Check the month, or that this is the right Joiners file.`,
    );
  }

  // Dedupe (keep last) AFTER validation so counts above describe the raw file.
  const leadDedupe = dedupeByMember(leadsIn);
  const joinsWithDate = joinsIn.map((r, i) => ({ ...r, iso: joinDates.get(i) ?? '' }));
  const joinDedupe = dedupeByMember(joinsWithDate);
  if (leadDedupe.dropped > 0) {
    warnings.push(`${leadDedupe.dropped} repeated member number${leadDedupe.dropped === 1 ? '' : 's'} in the New Prospects file were counted once.`);
  }
  if (joinDedupe.dropped > 0) {
    warnings.push(`${joinDedupe.dropped} repeated member number${joinDedupe.dropped === 1 ? '' : 's'} in the Joiners file were counted once.`);
  }

  const joinedMembers = new Set(joinDedupe.rows.map((r) => r.memberNumber.toUpperCase()));
  const leadMembers = new Set(leadDedupe.rows.map((r) => r.memberNumber.toUpperCase()));
  const leadsPrepared: PreparedLead[] = leadDedupe.rows.map((r) => {
    const d = classifyDigital(r.source, r.contactType, brand);
    return {
      ...r,
      isDigital: d.isDigital,
      digitalClass: d.digitalClass,
      channel: d.channel,
      untagged: d.untagged,
      sourceLabel: d.sourceLabel,
      isCohortJoin: joinedMembers.has(r.memberNumber.toUpperCase()),
    };
  });
  const joinsPrepared: PreparedJoin[] = joinDedupe.rows.map((r) => {
    const d = classifyDigital(r.source, r.contactType, brand);
    return {
      memberNumber: r.memberNumber,
      status: r.status,
      source: r.source,
      contactType: r.contactType,
      membershipType: r.membershipType,
      joiningDate: r.iso,
      isDigital: d.isDigital,
      digitalClass: d.digitalClass,
      channel: d.channel,
      sourceLabel: d.sourceLabel,
      fromMonthLead: leadMembers.has(r.memberNumber.toUpperCase()),
    };
  });

  // Statuses: classify, and SHOW what we do not recognise.
  const allStatuses = [...leadsPrepared.map((r) => r.status), ...joinsPrepared.map((r) => r.status)];
  const unknown = new Map<string, number>();
  let blankStatus = 0;
  for (const s of allStatuses) {
    const c = classifyStatus(s, brand);
    if (c === 'unknown') unknown.set(s, (unknown.get(s) ?? 0) + 1);
    if (c === 'blank') blankStatus += 1;
  }
  const unknownStatuses = [...unknown.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort(byCountThenName);
  if (unknownStatuses.length > 0) {
    warnings.push(
      `${unknownStatuses.length} Status value${unknownStatuses.length === 1 ? ' is' : 's are'} not one we recognise. They are stored as they are and not counted as joined.`,
    );
  }

  const sourceTally = tally(leadsPrepared, (r) => `${r.sourceLabel}\u0000${r.isDigital ? (r.digitalClass ?? '') : ''}`);
  const sources: SourceCount[] = [...sourceTally.entries()]
    .map(([k, count]) => {
      const [value, cls] = k.split('\u0000') as [string, string];
      return {
        value,
        count,
        isDigital: cls !== '',
        digitalClass: (cls === '' ? null : cls) as DigitalClass | null,
      };
    })
    .sort(byCountThenName);
  const contactTally = tally(
    leadsPrepared,
    (r) => `${r.contactType}\u0000${classifyDigital('', r.contactType, brand).isDigital ? '1' : '0'}`,
  );
  const contactTypes: ContactTypeCount[] = [...contactTally.entries()]
    .map(([k, count]) => {
      const [value, flag] = k.split('\u0000') as [string, string];
      return { value, count, isDigital: flag === '1' };
    })
    .sort(byCountThenName);

  const valuesRemoved =
    [...realLeads, ...realJoins].filter((r) => looksLikePii(r.source)).length +
    [...realLeads, ...realJoins].filter((r) => looksLikePii(r.contactType)).length;
  const otherTally = tally(
    [...leadsPrepared, ...joinsPrepared].filter((r) => r.channel === 'other'),
    (r) => `${r.source}\u0000${r.contactType}`,
  );
  const otherValues: OtherValueCount[] = [...otherTally.entries()]
    .map(([k, count]) => {
      const [source, contactType] = k.split('\u0000') as [string, string];
      return { source, contactType, count };
    })
    .sort((a, b) => b.count - a.count || a.source.localeCompare(b.source) || a.contactType.localeCompare(b.contactType));

  const analysis: ClubwiseUploadAnalysis = {
    valuesRemoved,
    testRecordsSkipped,
    otherValues,
    errors,
    warnings,
    counts: {
      leads: leadsPrepared.length,
      monthJoins: joinsPrepared.length,
      cohortJoins: leadsPrepared.filter((r) => r.isCohortJoin).length,
      leadsWithJoinedStatus: leadsPrepared.filter((r) => classifyStatus(r.status, brand) === 'joined').length,
      digitalLeads: leadsPrepared.filter((r) => r.isDigital).length,
      digitalJoins: joinsPrepared.filter((r) => r.isDigital).length,
    },
    duplicates: { leads: leadDedupe.dropped, joins: joinDedupe.dropped },
    unknownStatuses,
    blankStatus,
    sources,
    contactTypes,
  };

  return {
    analysis,
    prepared: errors.length === 0 ? { leads: leadsPrepared, joins: joinsPrepared, dataTo } : null,
  };
}
